import React from 'react';
import { Breadcrumb, BreadcrumbProps } from '../../components/breadcrumb/Breadcrumb';
import { Review, ReviewProps } from '../../components/review/Review';
import { ProductBanner, ProductBannerProps } from '../../components/product-banner/ProductBanner';
import {
    checkedEnrollmentButtonDomId,
    Enrollment,
    enrollmentSectionDomId,
    EnrollmentProps,
} from '../../components/enrollment/Enrollment';
import { SectionWithHtmlContent, SectionWithHtmlContentProps } from '../../components/common/SectionWithHtmlContent';
import {
    RelatedProductsSection,
    RelatedProductsSectionProps,
} from '../../components/related-products/RelatedProductsSection';
import { StickyNav } from '../../components/sticky/StickyNav';
import { Responsive } from '../../components/common/Responsive';
import { StickyBanner } from '../../components/sticky/StickyBanner';
import { FaqSection, FaqSectionProps } from '../../components/faq/FaqSection';
import { OutlineSection, OutlineSectionProps } from '../../components/outline/OutlineSection';
import { useIsIE } from '../../hooks/useIsIE';
import { AjaxLeadFormModel, SiteSettingsModel } from '../../models/globals';
import { isCtpOrCtpBundle } from '../../utilities/ProductUtilities';

interface ProductProps {
    banner: ProductBannerProps;
    enrollment: Omit<EnrollmentProps, 'leadFormModel'>;
    breadcrumb: BreadcrumbProps;
    faq: FaqSectionProps;
    instructors: SectionWithHtmlContentProps;
    objectives: SectionWithHtmlContentProps;
    outline: OutlineSectionProps;
    requirements: SectionWithHtmlContentProps;
    reviews: ReviewProps;
    relatedProducts: RelatedProductsSectionProps;
    moreAbout: SectionWithHtmlContentProps;
    leadFormModel: AjaxLeadFormModel;
    siteSettings?: SiteSettingsModel;
}

const Product: React.FC<ProductProps> = ({
    banner,
    enrollment,
    breadcrumb,
    faq,
    instructors,
    objectives,
    outline,
    requirements,
    reviews,
    relatedProducts,
    moreAbout,
    leadFormModel,
    siteSettings,
}) => {
    const { activeSections, refs, stickyBannerHeight, stickyNav, topReached } = useStickyNav();
    const stickyProps = useMultipleEnrollmentOptions(enrollment);
    const isConversionEnabled = enrollment.products[0].isConversionEnabled;

    return (
        /* pb-3 indicates there's content behind the sticky banner when page is scrolled to the bottom */
        <div className="pb-3">
            <Breadcrumb {...breadcrumb} />
            <ProductBanner {...banner} />
            <Enrollment {...enrollment} leadFormModel={leadFormModel} siteSettings={siteSettings} />
            <Responsive className="sticky-top" testIdSuffix="sticky-nav" viewMode="desktop-and-tablet">
                <StickyBanner
                    addToCartGtmAnalyticsData={stickyProps.addToCartGtmAnalyticsData}
                    addToCartLink={stickyProps.addToCartLink}
                    className={topReached ? 'd-block' : 'd-none'}
                    height={stickyBannerHeight}
                    isConversionEnabled={isConversionEnabled}
                    isCtpOrCtpBundle={isCtpOrCtpBundle(enrollment.products[0])}
                    name={banner.name}
                    leadFormModel={leadFormModel}
                />

                <StickyNav
                    ref={stickyNav}
                    sections={[
                        objectives,
                        outline,
                        instructors,
                        requirements,
                        moreAbout,
                        faq,
                        reviews,
                        relatedProducts,
                    ]}
                    sectionsInView={activeSections}
                />
            </Responsive>
            <SectionWithHtmlContent {...objectives} className="objectives" pageSectionRef={refs.objectivesRef} />
            <OutlineSection {...outline} pageSectionRef={refs.outlineRef} />
            <SectionWithHtmlContent {...instructors} pageSectionRef={refs.instructorsRef} />
            <SectionWithHtmlContent {...requirements} pageSectionRef={refs.requirementsRef} />
            <SectionWithHtmlContent {...moreAbout} pageSectionRef={refs.moreAboutRef} />
            <FaqSection {...faq} pageSectionRef={refs.faqRef} />
            <Review {...reviews} pageSectionRef={refs.reviewsRef} />
            <RelatedProductsSection
                {...relatedProducts}
                className="related-products"
                pageSectionRef={refs.relatedProductsRef}
            />
        </div>
    );
};

function useMultipleEnrollmentOptions(enrollment: Omit<EnrollmentProps, 'leadFormModel'>) {
    const { products } = enrollment;
    const [primaryProduct] = products;
    const showMultipleEnrollmentOptions = products.length > 1;
    const addToCartLink = {
        ...primaryProduct.addToCartLink,
        url: showMultipleEnrollmentOptions ? `#${enrollmentSectionDomId}` : primaryProduct.addToCartLink.url,
    };
    const addToCartGtmAnalyticsData = showMultipleEnrollmentOptions
        ? undefined
        : enrollment.products[0].addToCartGtmAnalyticsData;

    React.useEffect(() => {
        if (showMultipleEnrollmentOptions) {
            window.addEventListener('hashchange', focusActiveEnrollmentOption);

            return function cleanup() {
                window.removeEventListener('hashchange', focusActiveEnrollmentOption);
            };
        }

        function focusActiveEnrollmentOption() {
            if (window.location.hash === `#${enrollmentSectionDomId}`) {
                document.getElementById(checkedEnrollmentButtonDomId)?.focus();
                // clear the hash from the url bar so repeat events work the same way
                window.history.replaceState({}, '', window.location.href.replace(window.location.hash, ''));
            }
        }
    }, [showMultipleEnrollmentOptions]);

    return {
        addToCartGtmAnalyticsData,
        addToCartLink,
    };
}

function useStickyNav() {
    const objectivesRef = React.useRef<HTMLDivElement>(null);
    const outlineRef = React.useRef<HTMLDivElement>(null);
    const instructorsRef = React.useRef<HTMLDivElement>(null);
    const requirementsRef = React.useRef<HTMLDivElement>(null);
    const moreAboutRef = React.useRef<HTMLDivElement>(null);
    const faqRef = React.useRef<HTMLDivElement>(null);
    /*const certificationRef = React.useRef<HTMLDivElement>(null);*/
    const reviewsRef = React.useRef<HTMLDivElement>(null);
    const relatedProductsRef = React.useRef<HTMLDivElement>(null);
    const stickyBannerHeight = 170;
    const stickyNav = React.useRef<HTMLDivElement>(null);
    const [topReached, setTopReached] = React.useState(false);
    const stickyNavTargetStyle = React.useRef<CSSStyleDeclaration>();
    const [totalStickyHeight, setTotalStickyHeight] = React.useState(0);
    const [activeSections, setActiveSections] = React.useState<Record<string, boolean>>({});
    const isIE = useIsIE();

    React.useEffect(() => {
        setStickyHeightAndTopReached();

        window.addEventListener('scroll', setStickyHeightAndTopReached);
        window.addEventListener('resize', setStickyHeightAndTopReached);

        return function cleanup() {
            window.removeEventListener('scroll', setStickyHeightAndTopReached);
            window.removeEventListener('resize', setStickyHeightAndTopReached);
        };

        function setStickyHeightAndTopReached() {
            if (stickyNav.current) {
                const isDesktopOrTablet = window.innerWidth > 767;
                const { height, top } = stickyNav.current.getBoundingClientRect();

                setTotalStickyHeight(isDesktopOrTablet ? stickyBannerHeight + height : 0);

                setTopReached(!isIE && isDesktopOrTablet && Math.round(top) <= stickyBannerHeight);
            }
        }
    }, [isIE]);

    React.useEffect(
        function adjustAnchorOffsets() {
            stickyNavTargetStyle.current ??= getStyleDeclaration('.sticky-nav-target::before', 'site.css');
            stickyNavTargetStyle.current ??= {} as CSSStyleDeclaration; // prevent crash and retries if not found

            stickyNavTargetStyle.current.height = `${totalStickyHeight}px`;
            stickyNavTargetStyle.current.marginTop = `${-totalStickyHeight}px`;

            function getStyleDeclaration(
                selectorText: string,
                styleSheetTitle: string,
            ): CSSStyleDeclaration | undefined {
                const styleSheet = Array.from(document.styleSheets).find((sheet) => sheet.title === styleSheetTitle);
                const rule =
                    styleSheet &&
                    Array.from(styleSheet.cssRules).find(
                        (cssRule: CSSRule) => (cssRule as CSSStyleRule).selectorText === selectorText,
                    );
                return (rule as CSSStyleRule)?.style;
            }
        },
        [totalStickyHeight],
    );

    React.useEffect(() => {
        setIsScrolledIntoView();

        window.addEventListener('scroll', setIsScrolledIntoView);

        return function cleanup() {
            window.removeEventListener('scroll', setIsScrolledIntoView);
        };

        function setIsScrolledIntoView() {
            [
                objectivesRef,
                outlineRef,
                instructorsRef,
                requirementsRef,
                moreAboutRef,
                faqRef,
                /*certificationRef,*/
                reviewsRef,
                relatedProductsRef,
            ].forEach(({ current: pageSectionRef }) => {
                if (pageSectionRef?.dataset.domId) {
                    const { paddingBottom, paddingTop } = window.getComputedStyle(pageSectionRef);
                    const { clientHeight, offsetTop } = pageSectionRef;
                    const scrollPosition =
                        window.pageYOffset +
                        totalStickyHeight +
                        (parseInt(paddingBottom, 10) || 0) +
                        (parseInt(paddingTop, 10) || 0);

                    const { domId } = pageSectionRef.dataset;
                    const isActive = offsetTop <= scrollPosition && offsetTop + clientHeight > scrollPosition;

                    setActiveSections((sections) => {
                        const prevIsActive = sections[domId];
                        const isChanged = prevIsActive !== isActive;

                        return isChanged ? { ...sections, [domId]: isActive } : sections;
                    });
                }
            });
        }
    }, [totalStickyHeight]);

    return {
        activeSections,
        refs: {
            objectivesRef,
            outlineRef,
            instructorsRef,
            requirementsRef,
            moreAboutRef,
            faqRef,
            reviewsRef,
            relatedProductsRef,
        },
        stickyBannerHeight,
        stickyNav,
        topReached,
    };
}

export { Product, ProductProps };
